import { Link } from 'react-router-dom'

import { Badge, BarChart, Card, Kpi, Loaded, Notice, Page, StackedBar, Table } from '../components/ui'
import { get } from '../lib/api'
import { count, money, percent, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Billing = {
  mrr_usd_estimate: number
  monthly_price_usd: number
  tiers: Record<string, number>
  stores: Record<string, number>
  active_trials: number
  cancelled: number
  conversion_rate: number
  receipt_validation_failures: { user_id: string; store: string; tier: string }[]
}

export function Billing() {
  const billing = useResource((signal) => get<Billing>('/v1/admin/billing', undefined, signal))

  return (
    <Page
      title="Subscriptions"
      subtitle="Entitlement state as this system understands it. The stores are the source of truth for money; this screen is the source of truth for who has Pro."
    >
      <Loaded resource={billing}>
        {(data) => (
          <>
            <Notice>
              <strong>MRR here is an indicator, not an accounting figure.</strong> It is active Pro
              subscriptions × the ${data.monthly_price_usd.toFixed(2)} list price — it does not know
              about store commission, tax, currency, promotional pricing, or partial periods. Take
              revenue numbers from App Store Connect and Google Play Console, not from here.
            </Notice>

            <div className="grid grid--kpi">
              <Kpi
                label="MRR · indicative"
                value={money(data.mrr_usd_estimate)}
                tone="green"
                foot={`at $${data.monthly_price_usd.toFixed(2)} list`}
              />
              <Kpi label="Conversion" value={percent(data.conversion_rate)} tone="lime" />
              <Kpi label="Active trials" value={count(data.active_trials)} tone="cyan" />
              <Kpi
                label="Cancelled"
                value={count(data.cancelled)}
                tone={data.cancelled > 0 ? 'orange' : undefined}
              />
              <Kpi
                label="Receipt failures"
                value={count(data.receipt_validation_failures.length)}
                tone={data.receipt_validation_failures.length > 0 ? 'alert' : undefined}
                foot="Pro entitlement with no receipt on file"
              />
            </div>

            <div className="grid grid--2">
              <Card title="Tier split">
                <StackedBar
                  segments={Object.entries(data.tiers).map(([tier, n]) => ({
                    label: titleCase(tier),
                    value: n,
                  }))}
                  format={(v) => count(v)}
                />
              </Card>
              <Card title="By store" hint="Where the entitlement was purchased">
                <BarChart
                  color="var(--lime)"
                  data={Object.entries(data.stores).map(([store, n]) => ({
                    label: titleCase(store),
                    value: n,
                  }))}
                />
              </Card>
            </div>

            <Card
              title="Receipt validation failures"
              hint="Accounts holding Pro with nothing to prove it — each one is either a bug or a bypass"
              flush
            >
              <Table
                rows={data.receipt_validation_failures}
                rowKey={(row) => row.user_id}
                empty="Every Pro entitlement has a receipt on file."
                columns={[
                  {
                    key: 'user',
                    header: 'Account',
                    strong: true,
                    render: (row) => (
                      <Link className="mono" to={`/users/${row.user_id}`}>
                        {row.user_id}
                      </Link>
                    ),
                  },
                  {
                    key: 'store',
                    header: 'Store',
                    render: (row) =>
                      row.store ? <Badge>{titleCase(row.store)}</Badge> : <Badge tone="alert">None recorded</Badge>,
                  },
                  { key: 'tier', header: 'Tier', render: (row) => <Badge tone="lime">{titleCase(row.tier)}</Badge> },
                ]}
              />
            </Card>

            <Card title="Refunds and comps">
              <Notice tone="warn">
                Refunds are issued by Apple and Google, not here — a refund button in this console
                could not move money and would only desynchronise entitlement from the store's
                view. When a refund lands, the store's server notification revokes entitlement on
                the next receipt refresh. If you need to comp an account, use the store's
                promotional-offer flow so the receipt chain stays intact.
              </Notice>
            </Card>
          </>
        )}
      </Loaded>
    </Page>
  )
}
